/**
 * Deposit refund status — the single client-side derivation site.
 *
 * `get_contract_deposits_paginated` returns the gross deposit `amount` and the
 * summed `refunded_total`; status is derived here so the Records table, the
 * refund sheet and the insights surfaces can never disagree.
 *
 * Money rule: compare with an epsilon and round only at the display boundary.
 */

/** Half a pesewa — below this a difference is float dirt, not money. */
export const REFUND_EPSILON = 0.005;

export type DepositStatus = 'HELD' | 'PARTIALLY_REFUNDED' | 'REFUNDED';

/**
 * Vocabulary emitted by `get_contract_insights` for the same concept
 * ('PAID' where the list says 'HELD' — the insights card speaks from the
 * driver's side of the ledger).
 */
export type DepositInsightStatus = 'PAID' | 'PARTIALLY_REFUNDED' | 'REFUNDED';

/** What is still refundable, clamped at 0 and rounded to the cent. */
export function remainingRefundable(amount: number, refundedTotal: number): number {
    return Math.max(0, Math.round((amount - refundedTotal) * 100) / 100);
}

export function getDepositStatus(amount: number, refundedTotal: number): DepositStatus {
    if (refundedTotal <= REFUND_EPSILON) return 'HELD';
    if (refundedTotal >= amount - REFUND_EPSILON) return 'REFUNDED';
    return 'PARTIALLY_REFUNDED';
}

export const DEPOSIT_STATUS_LABELS: Record<DepositStatus | 'PAID', string> = {
    HELD: 'Held',
    PAID: 'Paid',
    PARTIALLY_REFUNDED: 'Partly Refunded',
    REFUNDED: 'Refunded',
};

/** `<Badge variant="outline" className={...}>` styles, light + dark. */
export const DEPOSIT_STATUS_STYLES: Record<DepositStatus | 'PAID', string> = {
    HELD: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-400',
    PAID: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-400',
    PARTIALLY_REFUNDED: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-400',
    REFUNDED: 'border-purple-200 bg-purple-50 text-purple-700 dark:border-purple-800 dark:bg-purple-950/40 dark:text-purple-400',
};

/** A deposit not yet linked to a contract — neutral, not a refund state. */
export const UNASSIGNED_BADGE_STYLE =
    'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-400';

/** Convenience for `<Badge variant="outline" className={badge.className}>{badge.label}</Badge>`. */
export function getDepositStatusBadge(status: DepositStatus | DepositInsightStatus): {
    label: string;
    className: string;
} {
    return { label: DEPOSIT_STATUS_LABELS[status], className: DEPOSIT_STATUS_STYLES[status] };
}
